/**
 * Shared hooks for AI agent approvals.
 *
 * `useApproval(approvalId)` — fetches a single approval (used when only the
 *   id is known, e.g. inline chat tool calls).
 * `useApprovalActions(approval, options?)` — returns the resolver, loading
 *   state, and capability-scoped tooltips. Single source of truth for the
 *   approve / reject / always-approve mutation and tooltip resolution.
 */
import React, { useCallback, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost } from '../api/axiosInstance';
import {
  resolveAlwaysApproveScope,
  extractResourceName,
} from '../components/chat/approvalScope';
import type {
  Approval,
  ApprovalAction,
  ApprovalDecision,
  ApprovalResolution,
} from '../types/approval';

const APPROVALS_API = '/api/v1/ai-agents/approvals';

interface DataResponse<T> {
  data: T;
}

export const APPROVAL_KEYS = {
  detail: (id: string) => ['approval', id] as const,
  pending: () => ['octopus', 'pending-approvals'] as const,
  inbox: () => ['octopus', 'inbox-approvals'] as const,
  inboxCount: () => ['octopus', 'inbox-approvals-count'] as const,
  history: (agentId: string) => ['octopus', 'approval-history', agentId] as const,
  recent: (agentId: string) => ['octopus', 'recent-approvals', agentId] as const,
  dashboard: (agentId: string) => ['octopus', 'dashboard', agentId] as const,
  toolsSettings: (agentId: string) => ['octopus', 'tools-settings', agentId] as const,
  objectives: (agentId: string) => ['octopus', 'objectives', agentId] as const,
  scheduledTasks: (agentId: string) => ['octopus', 'scheduled-tasks', agentId] as const,
  /** Matches MEMORY_KEYS.briefingBlocks shape — invalidating ['memory','briefing']
   *  catches blocks, pending-edits, and subscriptions in one shot. */
  briefingAll: (agentId: string) => ['memory', 'briefing', agentId] as const,
};

const decisionMap: Record<ApprovalAction, ApprovalDecision> = {
  approve: 'approved',
  always_approve: 'always_approved',
  reject: 'rejected',
  edit: 'edited',
};

/** Fetch a single approval by id. */
export function useApproval(approvalId: string | null | undefined) {
  return useQuery({
    queryKey: APPROVAL_KEYS.detail(approvalId ?? ''),
    queryFn: async () => {
      const response = await apiGet<DataResponse<Approval>>(
        `${APPROVALS_API}/${approvalId}`,
      );
      return response.data;
    },
    enabled: !!approvalId,
    staleTime: 30 * 1000,
  });
}

interface ResolveVariables {
  approvalId: string;
  agentId: string;
  resolution: ApprovalResolution;
}

/** Underlying mutation — invalidates oversight queries on success. */
function useResolveApprovalMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ approvalId, resolution }: ResolveVariables) => {
      const response = await apiPost<DataResponse<Approval>>(
        `${APPROVALS_API}/${approvalId}/resolve`,
        {
          decision: decisionMap[resolution.action],
          edited_payload: resolution.edited_payload,
          rejection_reason: resolution.rejection_reason,
          resolution_note: resolution.resolution_note,
        },
      );
      return response.data;
    },
    onSuccess: (data, variables) => {
      const { agentId, resolution } = variables;
      queryClient.invalidateQueries({ queryKey: APPROVAL_KEYS.pending() });
      queryClient.invalidateQueries({ queryKey: APPROVAL_KEYS.inbox() });
      queryClient.invalidateQueries({ queryKey: APPROVAL_KEYS.inboxCount() });
      queryClient.invalidateQueries({ queryKey: APPROVAL_KEYS.history(agentId) });
      queryClient.invalidateQueries({ queryKey: APPROVAL_KEYS.recent(agentId) });
      queryClient.invalidateQueries({ queryKey: APPROVAL_KEYS.dashboard(agentId) });
      queryClient.invalidateQueries({ queryKey: APPROVAL_KEYS.detail(variables.approvalId) });

      if (resolution.action === 'always_approve') {
        queryClient.invalidateQueries({ queryKey: APPROVAL_KEYS.toolsSettings(agentId) });
        // Briefing-block tier upgrade refreshes Context tab queries
        // (briefingBlocks, pending-edits, subscriptions all live under this prefix)
        queryClient.invalidateQueries({ queryKey: APPROVAL_KEYS.briefingAll(agentId) });
      }
      const actionType = data?.action_type ?? '';
      if (actionType.startsWith('objectives')) {
        queryClient.invalidateQueries({ queryKey: APPROVAL_KEYS.objectives(agentId) });
      } else if (actionType.startsWith('schedules')) {
        queryClient.invalidateQueries({ queryKey: APPROVAL_KEYS.scheduledTasks(agentId) });
      }
    },
    // The global mutation-error handler shows the specific translated toast.
  });
}

interface UseApprovalActionsOptions {
  /** Called immediately when resolve starts, before backend confirms.
   *  Used by chat to optimistically collapse the inline pending banner. */
  onResolveStart?: (action: ApprovalAction) => void;
}

/**
 * Returns resolver + loading state + tooltips for an approval.
 * Pass `null`/`undefined` while data is loading — the returned hooks no-op.
 */
export function useApprovalActions(
  approval: Approval | null | undefined,
  options: UseApprovalActionsOptions = {},
) {
  const { t } = useTranslation(['aiChat', 'common']);
  const mutation = useResolveApprovalMutation();
  const [resolving, setResolving] = useState<ApprovalAction | null>(null);
  // Hold the latest onResolveStart in a ref so callers can pass an inline
  // arrow without breaking referential stability of `resolve`.
  const onResolveStartRef = useRef(options.onResolveStart);
  onResolveStartRef.current = options.onResolveStart;

  const toolName = approval && typeof approval.action_payload?._tool_name === 'string'
    ? (approval.action_payload._tool_name as string)
    : undefined;
  const resourceName = extractResourceName(approval?.action_payload ?? undefined);
  const alwaysScope = resolveAlwaysApproveScope(approval?.action_type, toolName, resourceName);

  const rawAlwaysApprove = t(alwaysScope.key, alwaysScope.params ?? {});
  const tooltips = {
    approve: t('aiChat:approvalTooltip.approve'),
    reject: t('aiChat:approvalTooltip.reject'),
    // Strip the <bold>...</bold> markup so native `title=""` attributes and
    // tests receive plain text.
    alwaysApprove: rawAlwaysApprove.replace(/<\/?bold>/g, ''),
  };
  // Rich-content variant: bolds the interpolated resource name. Use inside a
  // shadcn <TooltipContent> to render.
  const alwaysApproveNode: React.ReactNode = (
    <Trans
      i18nKey={alwaysScope.key}
      values={alwaysScope.params ?? {}}
      components={{ bold: <strong className="font-semibold" /> }}
    />
  );

  const resolve = useCallback(
    async (
      action: ApprovalAction,
      extras?: { rejection_reason?: string; edited_payload?: Record<string, unknown> },
    ) => {
      if (!approval || resolving) return;
      setResolving(action);
      onResolveStartRef.current?.(action);
      try {
        await mutation.mutateAsync({
          approvalId: approval.id,
          agentId: approval.agent_id,
          resolution: { action, ...extras },
        });
      } finally {
        setResolving(null);
      }
    },
    [approval, mutation, resolving],
  );

  return {
    resolve,
    resolving,
    tooltips,
    alwaysApproveNode,
    isReady: !!approval,
  };
}
